/**
 * 解析 TikTok 发品约束（后端 GET .../publish/standard）。
 *
 * Tiktok 没有 Shein 的 query-publish-fill-in-standard。
 * 供货价币种以 goods.add 的 currencyType（CNY / USD）为准，由 /publish/standard 返回。
 *
 * 兼容历史：若误传入 Shein fill_in_standard_list，仍可解析，但 Tiktok 路径不会产出这些字段。
 */

function unwrapRoot(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.info && typeof raw.info === 'object') return raw.info;
  if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) return raw.data;
  return raw;
}

function unwrapInfo(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  if (raw.info != null) return raw.info;
  return raw;
}

/**
 * @returns {Map<string, { fieldKey: string, required: boolean, show: boolean, raw: object }>}
 */
export function parseFillInStandardMap(raw) {
  const info = unwrapInfo(raw);
  const list = info?.fill_in_standard_list
    || info?.fillInStandardList
    || info?.list
    || [];
  const map = new Map();
  (Array.isArray(list) ? list : []).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const key = String(row.field_key || row.fieldKey || '').trim();
    if (!key) return;
    map.set(key, {
      fieldKey: key,
      required: row.required === true || row.required === 'true',
      show: row.show !== false && row.show !== 'false',
      raw: row,
    });
  });
  return map;
}

/** 缺省 key 视为不可填（安全默认，避免误传触发 spmp0003） */
export function getFieldRule(map, fieldKey) {
  const hit = map instanceof Map ? map.get(String(fieldKey || '').trim()) : null;
  if (!hit) {
    return { show: false, required: false, fieldKey: String(fieldKey || '') };
  }
  return {
    fieldKey: hit.fieldKey,
    show: Boolean(hit.show),
    required: Boolean(hit.required),
  };
}

/**
 * SKU 包装相关能力（件数 / 包装类型）。
 * @returns {{
 *   quantityInfo: { show: boolean, required: boolean, fieldKey: string },
 *   packageType: { show: boolean, required: boolean, fieldKey: string },
 *   standardMap: Map<string, object>,
 * }}
 */
export function resolveSkuPackageCapabilities(standardRaw) {
  const standardMap = parseFillInStandardMap(standardRaw);
  return {
    quantityInfo: getFieldRule(standardMap, 'quantity_info'),
    packageType: getFieldRule(standardMap, 'package_type'),
    standardMap,
  };
}

/** 无类目规范时的安全默认：不展示、不传 SKU 件数/包装类型 */
export const EMPTY_SKU_PACKAGE_CAPS = Object.freeze({
  quantityInfo: Object.freeze({ show: false, required: false, fieldKey: 'quantity_info' }),
  packageType: Object.freeze({ show: false, required: false, fieldKey: 'package_type' }),
});

function collectCurrencyNodes(root) {
  if (!root || typeof root !== 'object') return [];
  const candidates = [
    root.currency,
    root.Currency,
    root.currency_list,
    root.currencyList,
    root.currencies,
    root.currencyTypeOptions,
    root.currency_type_options,
    root?.data?.currency,
    root?.data?.currency_list,
    root?.data?.currencyList,
    root?.data?.currencies,
    root?.standard?.currency,
    root?.publish_standard?.currency,
  ];
  for (const node of candidates) {
    if (node == null) continue;
    if (Array.isArray(node) && node.length) return node;
    if (typeof node === 'string' && node.trim()) return [node];
    if (typeof node === 'object' && !Array.isArray(node)) {
      const keys = Object.keys(node);
      if (!keys.length) continue;
      if (node.code || node.currency || node.currency_code || node.currencyCode) {
        return [node];
      }
      return keys.map((key) => {
        const val = node[key];
        if (val && typeof val === 'object') {
          return { code: key, name: val.name || val.label || val.currency_name || key, ...val };
        }
        return { code: key, name: val == null ? key : String(val) };
      });
    }
  }

  // field_list / list 里 name=currency 的规范项
  const fieldLists = [
    root.field_list,
    root.fieldList,
    root.list,
    root?.data?.field_list,
    root?.data?.list,
  ];
  for (const list of fieldLists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const name = String(item.field || item.field_name || item.name || item.key || '').toLowerCase();
      if (name !== 'currency') continue;
      const opts = item.options || item.values || item.value_list || item.list || item.currency;
      if (Array.isArray(opts) && opts.length) return opts;
      if (typeof opts === 'string' && opts.trim()) return [opts];
    }
  }

  // 兜底：浅层扫描字段名含 currency 的数组
  for (const [key, val] of Object.entries(root)) {
    if (!/currency/i.test(key)) continue;
    if (Array.isArray(val) && val.length) return val;
  }
  return [];
}

function normalizeCurrencyEntry(entry) {
  if (entry == null) return null;
  if (typeof entry === 'string' || typeof entry === 'number') {
    const code = String(entry).trim();
    if (!code) return null;
    return { code, name: code };
  }
  if (typeof entry !== 'object') return null;
  const code = String(
    entry.code
    || entry.currency
    || entry.currency_code
    || entry.currencyCode
    || entry.abbr
    || entry.value
    || entry.key
    || '',
  ).trim();
  if (!code) return null;
  const name = String(
    entry.name
    || entry.label
    || entry.currency_name
    || entry.currencyName
    || entry.desc
    || code,
  ).trim();
  return { code, name };
}

/** @returns {{ code: string, name: string }[]} */
export function extractPublishCurrencies(raw) {
  const root = unwrapRoot(raw);
  const nodes = collectCurrencyNodes(root);
  const out = [];
  const seen = new Set();
  nodes.forEach((entry) => {
    const row = normalizeCurrencyEntry(entry);
    if (!row) return;
    const key = row.code.toUpperCase();
    // 保留平台原始缩写大小写；去重按大写
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
}

/**
 * 店铺默认标题语种。Tiktok 发品以 productI18nReqs / productName 为准，无 Shein default_language。
 */
export function extractDefaultLanguage(raw) {
  const root = unwrapRoot(raw);
  if (!root || typeof root !== 'object') return '';
  const direct = String(
    root.default_language
    || root.defaultLanguage
    || '',
  ).trim();
  if (direct) return direct;
  return '';
}

/**
 * 在发布规范允许的币种中选默认值。
 * preferred 若在列表中则优先；否则取第一个。
 */
export function pickPublishCurrency(currencies = [], preferred = '') {
  const list = Array.isArray(currencies) ? currencies : [];
  if (!list.length) return '';
  const want = String(preferred || '').trim().toUpperCase();
  if (want) {
    const hit = list.find((c) => String(c.code || '').trim().toUpperCase() === want);
    if (hit) return String(hit.code || '').trim().toUpperCase() || want;
  }
  return String(list[0].code || '').trim().toUpperCase();
}
